import { useEffect, useRef } from 'react'
import Container from '@mui/material/Container'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import Link from 'src/components/Link'
import StaticBreadcrumbs from 'src/components/Breadcrumbs/StaticBreadcrumbs'
import config from 'src/config'
import getTotals from 'src/functions/getTotals'
import formatEuro from 'src/functions/formatEuro'
import { hasFreightShippingItems } from 'src/functions/shippingAvailability'
import { pageBuilderPurchaseButtonSx } from 'src/theme/pageBuilderButtons'
import Redux from '../Redux'
import Seo from '../Seo'
import Lehr from '../Lehr'
import Zusammenfassung from '../Components/Zusammenfassung/Zusammenfassung'
import CartItem from './CartItem'
import CartRecommendations from './CartRecommendations'
import { getSurchargeProgress } from './cartPresentation'
import styles from './ModernCart.module.css'

function CheckoutButton({ disabled = false }: { disabled?: boolean }) {
  return disabled
    ? <Button variant='contained' fullWidth disabled size='large'>Sicher zur Kasse</Button>
    : <Link href='/kasse' passHref legacyBehavior>
      <Button component='a' variant='contained' fullWidth size='large' sx={pageBuilderPurchaseButtonSx} endIcon={<ArrowForwardIcon />}>Sicher zur Kasse</Button>
    </Link>
}

export default function ModernCart() {
  const { items, changeQty, deleteItem, couponObjekt } = Redux()
  const bar = useRef<HTMLDivElement>(null)
  const entries = Object.values(items)
  const empty = entries.length === 0
  const validCoupon = couponObjekt?.code && typeof couponObjekt.balance === 'string' && couponObjekt.balance.trim()
    && couponObjekt.shops?.includes(config.shop.shop) ? couponObjekt : undefined
  const totals = getTotals(items, undefined, validCoupon)
  const progress = getSurchargeProgress(totals.WarenkorbBrutto, totals.Mindermengenzuschlag, config.mindermengenzuschlag.entfaelltAb)
  const inquiryOnly = entries.some(item => item.inquireOnly || item.purchaseMode === 'inquiry')
  const stockExceeded = entries.some(item => item.onlyStock && item.qty > (item.stock || 0))
  const count = entries.reduce((sum, item) => sum + item.qty, 0)

  useEffect(() => {
    if (empty || !bar.current) return
    const bodyPadding = document.body.style.paddingBottom
    const scrollPadding = document.documentElement.style.scrollPaddingBottom
    const basePadding = parseFloat(getComputedStyle(document.body).paddingBottom) || 0
    const resize = () => {
      const height = bar.current?.getBoundingClientRect().height || 0
      document.body.style.paddingBottom = `${basePadding + height}px`
      document.documentElement.style.scrollPaddingBottom = `${height + 16}px`
    }
    resize()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize)
    if (bar.current) observer?.observe(bar.current)
    window.addEventListener('resize', resize)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', resize)
      document.body.style.paddingBottom = bodyPadding
      document.documentElement.style.scrollPaddingBottom = scrollPadding
    }
  }, [empty])

  if (empty) return <Lehr />

  return <Container maxWidth='lg' className={styles.cart} data-cart-variant='modern'>
    <Seo />
    <StaticBreadcrumbs items={[{ title: 'Warenkorb' }]} />
    <div className={styles.heading}>
      <div><h1>Dein Warenkorb</h1><p>{count} Artikel für dein Zuhause</p></div>
      <Link href='/'>Weiter einkaufen <span aria-hidden='true'>→</span></Link>
    </div>
    <div className={styles.layout}>
      <div className={styles.main}>
        {totals.Mindermengenzuschlag > 0 ? <section className={styles.progress} aria-label='Mindermengenzuschlag' aria-live='polite'>
          <strong>Noch {formatEuro(progress.remaining)} bis zum Wegfall des Mindermengenzuschlags</strong>
          <p>Ab {formatEuro(config.mindermengenzuschlag.entfaelltAb)} Warenwert entfällt der Zuschlag von {formatEuro(config.mindermengenzuschlag.betrag)}.</p>
          <div className={styles.progressTrack} role='progressbar' aria-label='Warenwert bis zum Wegfall des Mindermengenzuschlags' aria-valuemin={0} aria-valuemax={config.mindermengenzuschlag.entfaelltAb} aria-valuenow={Math.min(config.mindermengenzuschlag.entfaelltAb, totals.WarenkorbBrutto)} aria-valuetext={`Noch ${formatEuro(progress.remaining)}`}>
            <span style={{ width: `${progress.percent}%` }} />
          </div>
        </section> : config.mindermengenzuschlag.betrag > 0 && !hasFreightShippingItems(items) && <div className={styles.thresholdReached} role='status'><CheckCircleOutlineIcon fontSize='small' /> Dein Mindermengenzuschlag entfällt.</div>}
        <div className={styles.items}>
          {entries.map(item => <CartItem key={item.key} item={item} changeQty={changeQty} deleteItem={deleteItem} />)}
        </div>
        <CartRecommendations />
      </div>
      <aside className={styles.summary} aria-label='Bestellübersicht'>
        <h2>Deine Bestellübersicht</h2>
        <Zusammenfassung compact displayVersandkostenHinweis />
        {inquiryOnly && <Alert severity='info' sx={{ my: 2 }}>Für Artikel auf Anfrage ist keine direkte Online-Bestellung möglich. Bitte entferne diese Artikel und nutze die Anfragefunktion am Produkt.</Alert>}
        <div className={styles.desktopCheckout}><CheckoutButton disabled={inquiryOnly || stockExceeded} /></div>
        <p className={styles.secure}><LockOutlinedIcon /> Sicher und verschlüsselt bestellen</p>
      </aside>
    </div>
    <div ref={bar} className={styles.mobileCheckout} aria-label='Zur Kasse gehen'>
      <div className={styles.mobileCheckoutInner}>
        <div className={styles.mobileTotal}><span>Gesamtsumme inkl. MwSt.</span><strong aria-live='polite'>{formatEuro(totals.Gesamtsumme)}</strong></div>
        <CheckoutButton disabled={inquiryOnly || stockExceeded} />
        <p className={styles.secure}><LockOutlinedIcon /> Sicher und verschlüsselt bestellen</p>
      </div>
    </div>
  </Container>
}
